import React, { FC, memo, ReactElement, useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { Form } from 'react-bootstrap';
import i18next from 'i18next';
import { useExtendedAuth } from '../../../util/auth0';

type SaveTemplateModelProps = {
  show: boolean;
  onPositive: (isGlobalTemplate: boolean, templateName: string) => void;
  onNegative: () => void;
};

const SaveTemplateModel: FC<SaveTemplateModelProps> = ({ show, onPositive, onNegative, }: SaveTemplateModelProps): ReactElement => {
  const { isAdminUser } = useExtendedAuth();

  const [isGlobalTemplate, setIsGlobalTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkIsAdminUser = async (): Promise<void> => {
      const isAdmin = await isAdminUser();
      setIsAdmin(isAdmin);
    };

    void checkIsAdminUser();
  }, [isAdminUser]);

  useEffect(() => {
    if (show) {
      resetModalState();
    }
  }, [show]);

  const resetModalState = (): void => {
    setTemplateName('');
    setIsGlobalTemplate(false);
  };

  return (
    <Modal show={show} onHide={onNegative}>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title>
          <h2>{i18next.t('trainingPlanForm.saveAsTemplateTitle')}</h2>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form.Group>
          <Form.Label>
            {i18next.t('trainingPlan.saveAndPublishModal.templateLabel')}
          </Form.Label>

          <Form.Control
            type="text"
            name="templateName"
            id="templateName"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder={i18next.t('trainingPlan.saveAndPublishModal.templatePlaceHolder')}
          />

          {templateName.length <= 0 && (
            <div className="invalid-feedback d-block">
              {i18next.t('trainingPlan.validation.templateName')}
            </div>
          )}

          <p>
            <small>
              {i18next.t('trainingPlan.saveAndPublishModal.templateHelpText')}
            </small>
          </p>
        </Form.Group>

        {isAdmin && (
          <Form.Check
            type="checkbox"
            name="isGlobalTemplate"
            id="isGlobalTemplate"
            checked={isGlobalTemplate}
            onChange={(e) => setIsGlobalTemplate(e.target.checked)}
            label={i18next.t('trainingPlan.saveAndPublishModal.isGlobalLabel')}
          />
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          style={{ marginRight: 'auto' }}
          onClick={onNegative}
        >
          {i18next.t('generic.cancel')}
        </Button>

        <Button
          onClick={() => onPositive(isGlobalTemplate, templateName)}
          variant="danger"
          disabled={templateName.length <= 0}
        >
          {i18next.t('trainingPlanForm.saveAsTemplate')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default memo(SaveTemplateModel);
